// =============================================================================
// reminders-client.http.ts — Adaptateur HTTP RÉEL du RemindersClient (A-16).
// =============================================================================
// V-SUIVI-FOURN-relance. Câble la relance manuelle derrière le MÊME DI seam (RemindersClientProvider) :
// l'écran consomme l'interface, l'impl est substituée sans toucher l'UI.
//
// AUTORITÉ = API (NestJS, AdminSessionGuard) + worker. Cet adaptateur ne porte AUCUNE règle métier : POST,
// parse, mappe l'erreur. SÉCURITÉ : session admin = COOKIE httpOnly (credentials: 'include') ; MUTATION (POST)
// ⇒ double-submit CSRF — on ré-émet le cookie NON-httpOnly `admin_csrf` en en-tête `x-csrf-token`.

import {
  RemindersApiError,
  type RemindersClient,
  type SendRemindersInput,
  type SendRemindersResultDto,
} from '@/api/reminders-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
}

/** Nom du cookie CSRF NON-httpOnly posé au login admin (double-submit, cf. API CSRF_COOKIE). */
const CSRF_COOKIE_NAME = 'admin_csrf';

/** Lit la valeur du cookie CSRF `admin_csrf` (NON-httpOnly). `undefined` si absent (fail-closed côté serveur). */
function readCsrfToken(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === CSRF_COOKIE_NAME) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

export class HttpRemindersClient implements RemindersClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async sendReminders(
    input: SendRemindersInput,
  ): Promise<SendRemindersResultDto> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const csrf = readCsrfToken();
    if (csrf !== undefined) {
      headers['x-csrf-token'] = csrf;
    }
    // Corps SANS campaignId (server-set depuis l'URL) : mode + cycle + sélection selon le mode.
    const body: Record<string, unknown> = { mode: input.mode, cycle: input.cycle };
    if (input.mode === 'individual') {
      body['sirets'] = input.sirets ?? [];
    }
    if (input.mode === 'siren') {
      body['siren'] = input.siren;
    }
    // Contenu éditable (R15-COM-05) — n'inclure les champs QUE lorsqu'ils sont présents (absent →
    // template statique côté worker ; l'API valide/borne subject ≤ 200, body ≤ 5000, non vides).
    if (input.subject !== undefined) {
      body['subject'] = input.subject;
    }
    if (input.body !== undefined) {
      body['body'] = input.body;
    }

    const response = await fetch(
      `${this.baseUrl}/admin/campaigns/${encodeURIComponent(input.campaignId)}/reminders`,
      {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as SendRemindersResultDto;
  }

  /** Convertit une réponse non-OK en erreur applicative typée (message serveur tel quel + statut). */
  private async toApiError(response: Response): Promise<RemindersApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur la relance.`;
    return new RemindersApiError(message, response.status);
  }
}
